import React, { useEffect, useState, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";
import Navbar from "./components/Navbar";
import GlobalTestModeBanner from "./components/GlobalTestModeBanner";
import BuddyWidget from "./components/BuddyWidget";
import Footer from "./components/Footer";
import MembersFooter from "./components/MembersFooter";
import MobileBottomNav from "./components/MobileBottomNav";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import OfflineBanner from "./components/OfflineBanner";
import { WorkerLanguageProvider } from "./contexts/WorkerLanguageContext";
import ScrollToTop from "./components/ScrollToTop";
import PageTransition from "./components/PageTransition";
import Home from "./pages/Home";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SignUp from "./pages/SignUp";
import FindWorkers from "./pages/FindWorkers";
import BrowseJobs from "./pages/BrowseJobs";
import HowItWorksPage from "./pages/HowItWorksPage";
import AIPoweredPage from "./pages/AIPoweredPage";
import ContractorDashboard from "./pages/ContractorDashboard";
import WorkerDashboard from "./pages/WorkerDashboard";
import WorkerProfile from "./pages/WorkerProfile";
import JobDetails from "./pages/JobDetails";
import AboutUs from "./pages/AboutUs";
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import ContractorControlPanel from "./pages/ContractorControlPanel";
import WorkerControlPanel from "./pages/WorkerControlPanel";
import Messages from "./pages/Messages";
import PostJob from "./pages/PostJob";
import MyJobs from "./pages/MyJobs";
import MyApplications from "./pages/MyApplications";
import PaymentSuccess from "./pages/PaymentSuccess";
import SavedJobs from "./pages/SavedJobs";
import EscrowDashboard from "./pages/EscrowDashboard";
import EscrowDetails from "./pages/EscrowDetails";
import ProjectPlanning from "./pages/ProjectPlanning";
import ProjectPlanDetail from "./pages/ProjectPlanDetail";
import Invoices from "./pages/Invoices";
import CreateInvoice from "./pages/CreateInvoice";
import InvoiceDetail from "./pages/InvoiceDetail";
import InvoiceTemplates from "./pages/InvoiceTemplates";
import RecurringInvoices from "./pages/RecurringInvoices";
import Calendar from "./pages/Calendar";
import Reports from "./pages/Reports";
import TimeClock from "./pages/TimeClock";
import Timesheets from "./pages/Timesheets";
import Wallet from "./pages/Wallet";
import ClientPortal from "./pages/ClientPortal";
import ClientLogin from "./pages/ClientLogin";
import PricingPage from "./pages/PricingPage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminDisputes from "./pages/AdminDisputes";
import AdminClientErrors from "./pages/AdminClientErrors";
import JobAlerts from "./pages/JobAlerts";
import ContractorVerification from "./pages/ContractorVerification";
import AdminVerifications from "./pages/AdminVerifications";
import DocumentsHub from "./pages/DocumentsHub";
import DocumentDetail from "./pages/DocumentDetail";
import AuthCallback from "./pages/AuthCallback";
import TeamManagement from "./pages/TeamManagement";
import WorkerTeams from "./pages/WorkerTeams";
import CreateWorkerTeam from "./pages/CreateWorkerTeam";
import WorkerTeamDetails from "./pages/WorkerTeamDetails";
import BrowseWorkerTeams from "./pages/BrowseWorkerTeams";
import AIPricingTool from "./pages/AIPricingTool";
import ProposalView from "./pages/ProposalView";
import AIScheduler from "./pages/AIScheduler";
import VoiceTranslator from "./pages/VoiceTranslator";
import PublicContractorProfile from "./pages/PublicContractorProfile";
import QuotesDashboard from "./pages/QuotesDashboard";
import HomeownerFunnel from "./pages/HomeownerFunnel";
import ContractorFunnel from "./pages/ContractorFunnel";
import Marketplace from "./pages/Marketplace";
import MarketplaceListing from "./pages/MarketplaceListing";
import MarketplaceNew from "./pages/MarketplaceNew";
import MarketplaceMyListings from "./pages/MarketplaceMyListings";
import MarketplaceCategory from "./pages/MarketplaceCategory";
import MarketplaceOrders from "./pages/MarketplaceOrders";
import MarketplaceOrderSuccess from "./pages/MarketplaceOrderSuccess";
import SubscriptionPage from "./pages/SubscriptionPage";
import AdminSubscriptions from "./pages/AdminSubscriptions";
import AdminLeads from "./pages/AdminLeads";
import AdminOutreach from "./pages/AdminOutreach";
import AdminLeadHunter from "./pages/AdminLeadHunter";
import ContractorHunterLeads from "./pages/ContractorHunterLeads";
import AdminContactMessages from "./pages/AdminContactMessages";
import AdvertisePage from "./pages/AdvertisePage";
import AdminAds from "./pages/AdminAds";
import AdminChief from "./pages/AdminChief";
import ToolPage from "./pages/ToolPage";
import AdvertisingPromo from "./pages/AdvertisingPromo";
import AdStatsPage from "./pages/AdStatsPage";
import LeadsMarketplace from "./pages/LeadsMarketplace";
import LandingPage from "./pages/LandingPage";
import TrustCenter from "./pages/TrustCenter";
import FeatureTimeline from "./pages/FeatureTimeline";
import SiteProblems from "./pages/SiteProblems";
import ConcreteCalculator from "./pages/ConcreteCalculator";
import HomeownerDashboard from "./pages/HomeownerDashboard";
import ProjectTracker from "./pages/ProjectTracker";
import PublicProgress from "./pages/PublicProgress";
import JobTemplates from "./pages/JobTemplates";
import FavoriteWorkers from "./pages/FavoriteWorkers";
import BidComparison from "./pages/BidComparison";
import FindWork from "./pages/FindWork";
import CostGuides from "./pages/CostGuides";
import CostGuideDetail from "./pages/CostGuideDetail";
import HireIndex from "./pages/HireIndex";
import HireTradePage from "./pages/HireTradePage";
import HireTradeCityPage from "./pages/HireTradeCityPage";
import HiringGuides from "./pages/HiringGuides";
import HiringGuideDetail from "./pages/HiringGuideDetail";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Tutorials from "./pages/Tutorials";
import TutorialDetail from "./pages/TutorialDetail";
import TelegramApp from "./pages/TelegramApp";
import AIProjectChat from "./pages/AIProjectChat";
import Store from "./pages/Store";
import AdminStore from "./pages/AdminStore";
import { trackPageview } from "./utils/analytics";
import WorkerAvailability from "./pages/WorkerAvailability";
import ReferralsPage from "./pages/ReferralsPage";
import { Toaster } from "./components/ui/toaster";
import { WebSocketProvider, useWebSocketContext, useWsSubscribe } from "./contexts/WebSocketContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";

// Routes that should hide the global navbar/footer
const CLEAN_LAYOUT_ROUTES = ['/', '/homeowner-dashboard', '/get-quotes', '/find-work', '/join-as-worker', '/contractor-funnel', '/tg'];

function AppLayout() {
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userType, setUserType] = useState(null);
  const [wsNotification, setWsNotification] = useState(null);

  // First-party analytics: track every page view
  useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname]);

  // Canonical hardening: every route gets a clean canonical (no trailing slash, no query)
  useEffect(() => {
    const path = location.pathname.replace(/\/+$/, '') || '/';
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', `${window.location.origin}${path}`);
  }, [location.pathname]);

  // Apply saved dashboard theme (bold default / soft pastel)
  useEffect(() => {
    document.documentElement.classList.toggle('theme-bold', (localStorage.getItem('dashboard_theme') || 'bold') === 'bold');
  }, []);

  const handleWsMessage = useCallback((data) => {
    if (data.type === 'notification') {
      setWsNotification(data.notification);
    }
  }, []);

  useWsSubscribe(handleWsMessage);
  const { isConnected: wsConnected } = useWebSocketContext();

  useEffect(() => {
    const syncAuth = () => {
      const token = localStorage.getItem('token');
      const type = localStorage.getItem('userType');
      setIsLoggedIn(!!token);
      setUserType(token ? type : null);
    };
    syncAuth();
    // Re-sync on cross-tab storage events
    window.addEventListener('storage', syncAuth);
    // Re-sync on a custom in-tab auth event (dispatched on login/logout)
    window.addEventListener('auth-changed', syncAuth);
    return () => {
      window.removeEventListener('storage', syncAuth);
      window.removeEventListener('auth-changed', syncAuth);
    };
  }, [location.pathname]);

  const isCleanLayout = CLEAN_LAYOUT_ROUTES.includes(location.pathname) || location.pathname.startsWith('/project/') || location.pathname.startsWith('/progress/');
  const membersFooterRole =
    location.pathname === '/contractor-dashboard' ? 'contractor' :
    location.pathname === '/worker-dashboard' ? 'worker' : null;

  return (
    <>
      <GlobalTestModeBanner />
      {!isCleanLayout && (
        <Navbar 
          isLoggedIn={isLoggedIn} 
          userType={userType} 
          wsConnected={wsConnected}
          wsNotification={wsNotification}
        />
      )}
      <AppRoutes />
      {!isCleanLayout && (membersFooterRole ? <MembersFooter role={membersFooterRole} /> : <Footer />)}
      {(!isCleanLayout || location.pathname === '/homeowner-dashboard') && <MobileBottomNav />}
      <OfflineBanner />
      <PWAInstallPrompt />
      {!location.pathname.startsWith('/tg') && !location.pathname.startsWith('/project/') && !location.pathname.startsWith('/progress/') && <BuddyWidget />}
      <Toaster />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300">
        <BrowserRouter>
          <ScrollToTop />
          <WebSocketProvider>
            <SubscriptionProvider>
              <WorkerLanguageProvider>
                <AppLayout />
              </WorkerLanguageProvider>
            </SubscriptionProvider>
          </WebSocketProvider>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

// Separate component for routes to enable useLocation hook
function AppRoutes() {
  return (
    <PageTransition>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/trust" element={<TrustCenter />} />
        <Route path="/timeline" element={<FeatureTimeline />} />
        <Route path="/whats-new" element={<FeatureTimeline />} />
        <Route path="/site-problems" element={<SiteProblems />} />
        <Route path="/home" element={<Home />} />
        <Route path="/explore" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/register" element={<SignUp />} />
        <Route path="/tools/concrete-calculator" element={<ConcreteCalculator />} />
        <Route path="/tools/:slug" element={<ToolPage />} />
        <Route path="/find-workers" element={<FindWorkers />} />
        <Route path="/worker/:workerId" element={<WorkerProfile />} />
        <Route path="/browse-jobs" element={<BrowseJobs />} />
        <Route path="/job/:jobId" element={<JobDetails />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/powered-by-ai" element={<AIPoweredPage />} />
        <Route path="/contractor-dashboard" element={<ContractorDashboard />} />
        <Route path="/worker-dashboard" element={<WorkerDashboard />} />
        <Route path="/contractor-control-panel" element={<ContractorControlPanel />} />
        <Route path="/worker-control-panel" element={<WorkerControlPanel />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/post-job" element={<PostJob />} />
        <Route path="/my-jobs" element={<MyJobs />} />
        <Route path="/my-applications" element={<MyApplications />} />
        <Route path="/saved-jobs" element={<SavedJobs />} />
        <Route path="/escrow" element={<EscrowDashboard />} />
        <Route path="/escrow/:escrowId" element={<EscrowDetails />} />
        <Route path="/project-planning" element={<ProjectPlanning />} />
        <Route path="/project-planning/:planId" element={<ProjectPlanDetail />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/invoices/create" element={<CreateInvoice />} />
        <Route path="/invoices/templates" element={<InvoiceTemplates />} />
        <Route path="/invoices/recurring" element={<RecurringInvoices />} />
        <Route path="/invoices/:invoiceId" element={<InvoiceDetail />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/time-clock" element={<TimeClock />} />
        <Route path="/timesheets" element={<Timesheets />} />
        <Route path="/wallet" element={<Wallet />} />
        <Route path="/team" element={<TeamManagement />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/disputes" element={<AdminDisputes />} />
        <Route path="/admin/client-errors" element={<AdminClientErrors />} />
        <Route path="/job-alerts" element={<JobAlerts />} />
        <Route path="/verification" element={<ContractorVerification />} />
        <Route path="/admin/verifications" element={<AdminVerifications />} />
        <Route path="/documents" element={<DocumentsHub />} />
        <Route path="/documents/:id" element={<DocumentDetail />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/client-portal" element={<ClientPortal />} />
        <Route path="/client-login" element={<ClientLogin />} />
        <Route path="/worker-teams" element={<WorkerTeams />} />
        <Route path="/worker-teams/create" element={<CreateWorkerTeam />} />
        <Route path="/worker-teams/:teamId" element={<WorkerTeamDetails />} />
        <Route path="/browse-worker-teams" element={<BrowseWorkerTeams />} />
        <Route path="/ai-pricing" element={<AIPricingTool />} />
        <Route path="/proposal/:token" element={<ProposalView />} />
        <Route path="/ai-scheduler" element={<AIScheduler />} />
        <Route path="/translator" element={<VoiceTranslator />} />
        <Route path="/pro/:slug" element={<PublicContractorProfile />} />
        <Route path="/contractor/:slug" element={<PublicContractorProfile />} />
        <Route path="/quotes" element={<QuotesDashboard />} />
        <Route path="/get-quotes" element={<HomeownerFunnel />} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/account/subscription" element={<SubscriptionPage />} />
        <Route path="/admin/leads" element={<AdminLeads />} />
        <Route path="/admin/outreach" element={<AdminOutreach />} />
        <Route path="/admin/lead-hunter" element={<AdminLeadHunter />} />
        <Route path="/admin/messages" element={<AdminContactMessages />} />
        <Route path="/advertise" element={<AdvertisePage />} />
        <Route path="/advertising" element={<AdvertisingPromo />} />
        <Route path="/ad-stats/:token" element={<AdStatsPage />} />
        <Route path="/admin/ads" element={<AdminAds />} />
        <Route path="/admin/chief" element={<AdminChief />} />
        <Route path="/hunter-leads" element={<ContractorHunterLeads />} />
        <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
        <Route path="/leads-marketplace" element={<LeadsMarketplace />} />
        <Route path="/homeowner-dashboard" element={<HomeownerDashboard />} />
        <Route path="/project/:projectId" element={<ProjectTracker />} />
        <Route path="/progress/:token" element={<PublicProgress />} />
        <Route path="/job-templates" element={<JobTemplates />} />
        <Route path="/favorite-workers" element={<FavoriteWorkers />} />
        <Route path="/bid-comparison/:jobId" element={<BidComparison />} />
        <Route path="/join-as-worker" element={<FindWork />} />
        <Route path="/find-work" element={<Navigate to="/join-as-worker" replace />} />
        <Route path="/cost-guides" element={<Navigate to="/guides" replace />} />
        <Route path="/guides" element={<CostGuides />} />
        <Route path="/guides/:slug" element={<CostGuideDetail />} />
        <Route path="/hire" element={<HireIndex />} />
        <Route path="/hire/:slug" element={<HireTradePage />} />
        <Route path="/hire/:slug/:citySlug" element={<HireTradeCityPage />} />
        <Route path="/hiring-guides" element={<HiringGuides />} />
        <Route path="/hiring-guides/:slug" element={<HiringGuideDetail />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/tutorials" element={<Tutorials />} />
        <Route path="/tutorials/:slug" element={<TutorialDetail />} />
        <Route path="/tg" element={<TelegramApp />} />
        <Route path="/ai-project" element={<AIProjectChat />} />
        <Route path="/store" element={<Store />} />
        <Route path="/admin/store" element={<AdminStore />} />
        <Route path="/my-availability" element={<WorkerAvailability />} />
        <Route path="/referrals" element={<ReferralsPage />} />
        <Route path="/contractor-funnel" element={<ContractorFunnel />} />
        <Route path="/become-a-contractor" element={<ContractorFunnel />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/marketplace/listing/:id" element={<MarketplaceListing />} />
        <Route path="/marketplace/category/:slug" element={<MarketplaceCategory />} />
        <Route path="/marketplace/new" element={<MarketplaceNew />} />
        <Route path="/marketplace/my-listings" element={<MarketplaceMyListings />} />
        <Route path="/marketplace/orders" element={<MarketplaceOrders />} />
        <Route path="/marketplace/order/success" element={<MarketplaceOrderSuccess />} />
      </Routes>
    </PageTransition>
  );
}

export default App;
